import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ProjectSocketEvent } from '@trinity/chat';
import type { Socket } from 'socket.io-client';
import Toast from '@trinity/chat-components/components/Toast/index.js';
import { useProjectSocket } from './useProjectSocket';

interface UseWebSocketEventsParams {
  projectId: number;
  enabled?: boolean;
}

export const useWebSocketEvents = ({ projectId, enabled = true }: UseWebSocketEventsParams): Socket => {
  const socket = useProjectSocket(projectId);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket || !enabled) return;

    const handleTranscriptCleanupCompleted = (payload: {
      success: true;
      eventKey: string;
      result: unknown;
    }) => {
      console.log('Transcript cleanup completed:', payload);

      // Invalidate entities query to refresh the table
      queryClient.invalidateQueries({
        queryKey: ['get:projects/entities'],
      });

      // Show success notification
      Toast.success({
        title: 'Entity Changes Applied',
        body: 'Entity modifications have been successfully applied to your project.',
      });
    };

    const handleTranscriptCleanupFailed = (payload: {
      success: false;
      eventKey: string;
      error?: string;
    }) => {
      console.error('Transcript cleanup failed:', payload);

      // Show error notification
      Toast.error({
        title: 'Entity Modification Failed',
        body: payload.error || 'An unknown error occurred while applying entity changes.',
      });
    };

    // Add event listeners
    socket.on(ProjectSocketEvent.TranscriptCleanupCompleted, handleTranscriptCleanupCompleted);
    socket.on(ProjectSocketEvent.TranscriptCleanupFailed, handleTranscriptCleanupFailed);

    // Cleanup event listeners
    return () => {
      socket.off(ProjectSocketEvent.TranscriptCleanupCompleted, handleTranscriptCleanupCompleted);
      socket.off(ProjectSocketEvent.TranscriptCleanupFailed, handleTranscriptCleanupFailed);
    };
  }, [socket, enabled, queryClient]);

  return socket;
};